import type { AssetAllocationDisplay, AssetAllocationPeriod, AssetAllocationScopeType, AssetAllocationSnapshot } from "./asset-allocation.ts";
import { emptyPortfolioAnalytics } from "./portfolio-analytics.ts";
import type { Household, Member, Platform, PortfolioState } from "./portfolio-types.ts";
import type { WorkspaceRouteKey } from "./workspace-navigation.ts";
import { workspaceRouteKeys } from "./workspace-navigation.ts";

export const workspacePageModelContractVersion = "31.10.0" as const;

export type WorkspacePageModelCommon = {
  household: Household;
  members: Member[];
  platforms: Platform[];
  currentUserEmail: string;
};

export type WorkspacePageShellSummary = {
  latestNetWorthMonth: string | null;
  latestActivityAt: string | null;
  valuation: {
    status: "empty" | "success" | "degraded" | "failed";
    requiredCount: number;
    updatedCount: number;
    cachedCount: number;
    failedCount: number;
    commonAsOf: string | null;
    latestAsOf: string | null;
  };
};

export type PositionsAllocationPageData = {
  kind: "positions-allocation";
  shell: WorkspacePageShellSummary;
  query: {
    scopeType: AssetAllocationScopeType;
    scopeId: string;
    period: AssetAllocationPeriod;
    anchor: string;
    display: AssetAllocationDisplay;
  };
  snapshot: AssetAllocationSnapshot;
};

export type OverviewRouteState = Pick<PortfolioState,
  | "fxRates" | "holdingMaturities" | "householdIncomeRecords" | "importedPositions"
  | "investmentPlans" | "monthlyNetWorth" | "overviewSpendingRows" | "portfolioAnalytics"
  | "portfolioAnalyticsByMember" | "positionGroupFlowSeries" | "positionGroups"
  | "redemptionFeeLots" | "sourceTransactions" | "strategySleeves"
  | "assetExposures"
>;

export type NetWorthRouteState = Pick<PortfolioState, "fxRates" | "monthlyNetWorth">;

export type PositionsRouteState = Pick<PortfolioState,
  | "cashAccounts" | "fxRates" | "holdingEpisodes" | "holdingMaturities" | "importedPositions" | "investmentOrders"
  | "manualWealthProfiles" | "portfolioAnalytics" | "portfolioAnalyticsByMember"
  | "positionGroups" | "redemptionFeeLots" | "redemptionLagSettings" | "strategySleeves"
  | "assetExposures" | "tigerConnections"
>;

export type CashflowRouteState = Pick<PortfolioState,
  "cashAccounts" | "fxRates" | "householdIncomeRecords" | "investmentOrders"
>;

export type FxRouteState = Pick<PortfolioState,
  "cashAccounts" | "fxRates" | "importedPositions" | "tigerConnections"
>;

export type ResearchIndexRouteState = Pick<PortfolioState, "syncRuns">;
export type ResearchWealthRouteState = Pick<PortfolioState, "bankWealthProducts" | "importedPositions">;
export type DataConnectionsRouteState = Pick<PortfolioState,
  | "eiaConnection" | "fredConnection" | "fxRates" | "mailbox" | "openAiConnection"
  | "proxyEgressConnection" | "tigerConnections" | "tigerPositions"
>;
export type DataImportRouteState = Pick<PortfolioState, "institutionConnections" | "mailbox" | "statementImportRuns">;
export type DataRecordsRouteState = Pick<PortfolioState, "investmentOrders">;
export type DataBackupRouteState = Pick<PortfolioState, "accessSecurity">;
export type DataSecurityRouteState = Pick<PortfolioState, "accessSecurity" | "auditLogs">;
export type DataSettingsRouteState = Pick<PortfolioState, "accessSecurity" | "redemptionLagSettings">;

type PortfolioRoutePageData<TKind extends Exclude<WorkspaceRouteKey, "positions-allocation">, TState> = {
  kind: TKind;
  shell: WorkspacePageShellSummary;
  state: TState;
};

export type WorkspacePortfolioPageData =
  | PortfolioRoutePageData<"overview", OverviewRouteState>
  | PortfolioRoutePageData<"networth", NetWorthRouteState>
  | PortfolioRoutePageData<"positions-overview" | "positions-holdings" | "positions-maturities", PositionsRouteState>
  | PortfolioRoutePageData<"cashflow", CashflowRouteState>
  | PortfolioRoutePageData<"fx", FxRouteState>
  | PortfolioRoutePageData<"research-funds" | "research-macro" | "research-macro-cn", ResearchIndexRouteState>
  | PortfolioRoutePageData<"research-wealth", ResearchWealthRouteState>
  | PortfolioRoutePageData<"data-connections", DataConnectionsRouteState>
  | PortfolioRoutePageData<"data-import", DataImportRouteState>
  | PortfolioRoutePageData<"data-records", DataRecordsRouteState>
  | PortfolioRoutePageData<"data-backup", DataBackupRouteState>
  | PortfolioRoutePageData<"data-security", DataSecurityRouteState>
  | PortfolioRoutePageData<"data-settings", DataSettingsRouteState>;

export type WorkspacePageModelData = PositionsAllocationPageData | WorkspacePortfolioPageData;

export type WorkspacePageModelEnvelope<TData extends WorkspacePageModelData = WorkspacePageModelData> = {
  contractVersion: typeof workspacePageModelContractVersion;
  routeKey: WorkspaceRouteKey;
  generatedAt: string;
  revisions: Record<string, number>;
  common: WorkspacePageModelCommon;
  data: TData;
};

export type PositionsAllocationPageModel = WorkspacePageModelEnvelope<PositionsAllocationPageData>;
export type WorkspacePortfolioPageModel = WorkspacePageModelEnvelope<WorkspacePortfolioPageData>;

export function isWorkspacePageModel(value: unknown): value is WorkspacePageModelEnvelope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<WorkspacePageModelEnvelope>;
  return candidate.contractVersion === workspacePageModelContractVersion
    && typeof candidate.routeKey === "string"
    && workspaceRouteKeys.has(candidate.routeKey as WorkspaceRouteKey)
    && candidate.data?.kind === candidate.routeKey;
}

export function isPositionsAllocationPageModel(value: WorkspacePageModelEnvelope): value is PositionsAllocationPageModel {
  return value.routeKey === "positions-allocation" && value.data.kind === "positions-allocation";
}

export function portfolioStateFromWorkspacePageModel(model: WorkspacePageModelEnvelope): PortfolioState | null {
  if (isPositionsAllocationPageModel(model)) return null;
  if (!("state" in model.data)) return null;
  const common = model.common;
  const emptyConnection = {
    configured: false,
    keyMask: null,
    lastVerifiedAt: null,
    validationStatus: "not_configured" as const,
    validationMessage: null,
    updatedAt: null,
    canEdit: false,
  };
  const base: PortfolioState = {
    household: common.household,
    members: common.members,
    platforms: common.platforms,
    syncRuns: [],
    auditLogs: [],
    monthlyNetWorth: [],
    statementImportRuns: [],
    sourceTransactions: [],
    overviewSpendingRows: [],
    investmentOrders: [],
    importedPositions: [],
    holdingEpisodes: [],
    portfolioAnalytics: emptyPortfolioAnalytics(),
    portfolioAnalyticsByMember: {},
    positionGroups: [],
    strategySleeves: [],
    assetExposures: [],
    investmentPlanVersions: [],
    redemptionLagSettings: { moneyMarketDays: 1, standardFundDays: 2, qdiiDays: 2, updatedAt: null },
    householdIncomeRecords: [],
    cashAccounts: [],
    investmentPlans: [],
    holdingMaturities: [],
    redemptionFeeLots: [],
    positionGroupFlowSeries: {},
    tigerConnections: [],
    tigerPositions: [],
    fxRates: [],
    bankWealthProducts: [],
    manualWealthProfiles: [],
    manualBalanceSnapshots: [],
    manualIncomePeriods: [],
    mailbox: { connection: null, messages: [], files: [], savedPasswords: [] },
    institutionConnections: { mode: "disabled", pilotProvider: null, capabilities: [], connections: [], recentRuns: [] },
    fredConnection: emptyConnection,
    eiaConnection: emptyConnection,
    proxyEgressConnection: {
      configured: false,
      subscriptionCount: 0,
      lastRefreshedAt: null,
      validationStatus: "not_configured",
      validationMessage: null,
      updatedAt: null,
      canEdit: false,
      totalNodes: 0,
      yahooAvailableNodes: 0,
      fredAvailableNodes: 0,
      gmailAvailableNodes: 0,
      currentYahooNode: null,
      currentFredNode: null,
      currentGmailNode: null,
      nodes: [],
    },
    openAiConnection: { ...emptyConnection, model: null },
    accessSecurity: {
      canView: false,
      events: [],
      summaries: [],
      settings: { accessRetentionDays: 90, businessRetentionDays: 730, updatedAt: null, actorEmail: null },
    },
    currentUserEmail: common.currentUserEmail,
  };
  return { ...base, ...model.data.state } as PortfolioState;
}

const investmentRouteKeys: WorkspaceRouteKey[] = [
  "overview",
  "positions-overview",
  "positions-holdings",
  "positions-allocation",
  "positions-maturities",
  "fx",
];

export type WorkspaceDomainRevisionScope =
  | "portfolio-facts"
  | "portfolio-market"
  | "asset-allocation"
  | "cashflow"
  | "research"
  | "connections"
  | "household-config";

export function workspaceRevisionScopesForAction(action: string): WorkspaceDomainRevisionScope[] {
  if (["add_platform", "update_platform", "update_member"].includes(action)) return ["household-config"];
  if (["save_monthly_net_worth", "save_monthly_net_worth_batch", "delete_monthly_net_worth"].includes(action)) {
    return ["portfolio-facts"];
  }
  if (/household_income|cash_funding_lot/.test(action)) return ["cashflow"];
  if (/mailbox|institution|fred|eia|proxy_egress|openai_connection|security_settings|security_records/.test(action)) {
    return ["connections"];
  }
  if (/bank_wealth_product/.test(action)) return ["research"];
  if (/position_group|strategy_sleeve|investment_plan/.test(action)) return ["asset-allocation"];
  if (/sync_platform_exchange_rates/.test(action)) return ["portfolio-market"];
  if (/tiger_connection/.test(action)) return ["connections"];
  if (/holding_maturity|redemption_lag/.test(action)) return ["portfolio-facts"];
  if (/manual_wealth|manual_balance|manual_income/.test(action)) return ["portfolio-facts", "portfolio-market"];
  return ["portfolio-facts", "portfolio-market"];
}

export function invalidatedWorkspaceRouteKeys(action: string): WorkspaceRouteKey[] {
  if (["add_platform", "update_platform", "update_member"].includes(action)) return [...workspaceRouteKeys];
  if (["save_monthly_net_worth", "save_monthly_net_worth_batch", "delete_monthly_net_worth"].includes(action)) {
    return ["overview", "networth"];
  }
  if (/household_income|cash_funding_lot/.test(action)) return ["overview", "cashflow", "fx"];
  if (/mailbox|institution|fred|eia|proxy_egress|openai_connection|tiger_connection|security_settings|security_records/.test(action)) {
    return ["data-connections", "data-import", "data-records", "data-security", "data-settings"];
  }
  if (/bank_wealth_product/.test(action)) return ["research-wealth", "positions-overview", "positions-holdings"];
  if (/investment_plan/.test(action)) return ["overview", "positions-overview", "positions-allocation"];
  if (/holding_maturity|redemption_lag/.test(action)) return ["positions-overview", "positions-holdings", "positions-maturities"];
  if (/position_group|strategy_sleeve/.test(action)) return ["overview", "positions-overview", "positions-holdings", "positions-allocation"];
  return investmentRouteKeys;
}

export type PortfolioMutationAck = {
  ok: true;
  notice: { text: string; error: false } | null;
  revisions: Record<string, number>;
  invalidatedRouteKeys: WorkspaceRouteKey[];
  result?: unknown;
};

export function isPortfolioMutationAck(value: unknown): value is PortfolioMutationAck {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<PortfolioMutationAck>;
  return candidate.ok === true
    && Array.isArray(candidate.invalidatedRouteKeys)
    && Boolean(candidate.revisions && typeof candidate.revisions === "object");
}
